import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  FileCheck,
  ArrowRight,
  Shield,
  Zap,
  Brain,
  BarChart3,
  Lock,
  Play,
  AlertTriangle,
  FileText,
  Search,
  Target,
  Network,
  Eye,
  Clock,
  Award,
  Scale,
  FileSearch
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { financePolicyCheckTestimonials } from '../../shared/testimonialData';

const FinancePolicyCheck: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Brain,
      title: 'Policy Engine & Rule Modeller ',
      description: 'Configure finance policies as modular rules (approval limits, expense categories, payment terms, vendor whitelists/blacklists) rather than static PDFs, with version control and audit history.'
    },
    {
      icon: FileSearch,
      title: 'Transaction Ingestion ',
      description: 'Pull live or batch data from ERP, HR, procurement, and expense systems to check invoices, journal entries, reimbursements, and purchase orders automatically.'
    },
    {
      icon: Shield,
      title: 'Real-Time Policy Checks ',
      description: 'Validate each transaction against active rules, highlight breaches, and route them to the right people for review, with clear explanations and recommended actions.'
    },
    {
      icon: AlertTriangle,
      title: '	Exception & Workflow Management ',
      description: 'Manage overrides and approvals through structured workflows that capture “who approved what and why,” creating a defensible trail for auditors and regulators.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Dashboards ',
      description: 'Visualize policy breaches by entity, region, cost center, or vendor; identify recurring issues; and measure how well controls are operating over time.'
    },
  ];

  const solutions = [
    {
      icon: Scale,
      title: 'Accounts Payable Compliance',
      description: 'Riskpoilt checks every invoice and payment against spending limits, approved vendors, duplicate payments, and payment-term rules. It reduces the number of manual AP approvals, flags suspected fraud or conflict-of-interest vendors, and helps teams standardize practices across regions.',
    },
    {
      icon: Lock,
      title: 'Employee Expenses & T&E Policy Enforcement',
      description: 'Expense claims are automatically matched to policy rules around categories, per-diem limits, travel classes, and approvals. Out-of-policy items are flagged before reimbursement, reducing awkward conversations, rework, and leakages in travel and entertainment budgets.',
    },
    {
      icon: FileText,
      title: 'Journal Entry & Closing Controls',
      description: 'During month-end and year-end, Riskpoilt screens manual journal entries for unusual patterns, missing explanations, and violations of approval tiers. Controllers see a consolidated list of “entries that need attention,” allowing them to focus on material risks instead of scanning every line by hand.',
    },
    {
      icon: Target,
      title: 'Procurement & Contract Governance',
      description: 'Purchase requisitions and contracts are checked against preferred suppliers, pricing bands, and contract clauses. The platform can highlight when spend is being fragmented to avoid approval thresholds or when long-tail suppliers are bypassing sourcing rules.',
    }
  ];

  const compliances = [
    {
      icon: Search,
      title: 'Secure Data Integration',
      description: 'Connections to ERP, HR, and expense systems use encrypted channels and hardened connectors. Only the minimal data required for policy checking is pulled, and access is governed by strict roles and permissions.',
      metric: 'Smart Analysis'
    },
    {
      icon: Network,
      title: 'Granular Access Controls',
      description: 'Finance users, auditors, and business approvers each see only the data and dashboards relevant to their role. Sensitive data (salary, personally identifiable information, strategic vendor terms) can be masked or restricted to specific user groups.',
      metric: 'Full Traceability'
    },
    {
      icon: Clock,
      title: 'Full Audit Trails & Evidence',
      description: 'Every policy change, rule activation, exception approval, and override is time-stamped and attributed to a user. This audit trail can be exported as evidence for internal and external audits, regulatory reviews, and SOX/IFC testing.',
      metric: 'Real-Time Alerts'
    },
    {
      icon: Award,
      title: 'Data Residency & Retention Options',
      description: 'Riskpoilt can be deployed in regions that align with your data-residency requirements, with configurable retention periods and deletion policies that mirror your internal governance and legal obligations.',
      metric: 'Exam Ready'
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Stronger Financial Controls with Less Manual Work',
      description: 'Automated checking means finance teams review a smaller, prioritized list of exceptions instead of manually scanning every document. This frees time for analysis and business partnering while tightening control.',
    },
    {
      icon: Shield,
      title: '	Reduced Policy Breaches, Fraud, and Leakage',
      description: 'Continuous monitoring catches risky transactions—split invoices, duplicate claims, off-contract spend—before they settle, rather than months later during an audit. That directly prevents cash leakage and reputational damage.',
    },
    {
      icon: BarChart3,
      title: '	Consistent Governance Across Entities and Regions',
      description: 'Riskpoilt provides a single policy engine that can handle local variations while maintaining global standards. This makes it easier to roll out new policies, integrate acquisitions, and benchmark behavior across the group.',
    },
    {
      icon: Eye,
      title: '	Faster, Cleaner Audits',
      description: 'With centralized evidence, clear exception histories, and analytics, internal and external audits become more about sampling logic and less about chasing documents. That can shorten audit cycles and reduce disruption to business teams.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-emerald-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 neon-glow">
                  <FileCheck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-emerald-400 uppercase tracking-wide">Compliance Automation</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-emerald-500 to-green-600 bg-clip-text text-transparent">Finance Policy Check</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Regulatory Compliance Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               The platform acts as a digital control layer between your business operations and your general ledger. It understands approval limits, spending rules, segregation of duties, vendor restrictions, and regional compliance requirements, then flags anything that doesn’t fit. Controllers, finance shared-service centers, procurement teams, and internal audit all connect to the same source of truth, reducing the risk of policy breaches, fraud, and expensive mistakes.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-green-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300"
                >
                  Request Compliance Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Overview
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-500">80%</div>
                  <div className="text-sm text-muted-foreground">Time Saved</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">100+</div>
                  <div className="text-sm text-muted-foreground">Regulations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Real-Time</div>
                  <div className="text-sm text-muted-foreground">Monitoring</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/FinancePolicy.png"
                    alt="Finance Policy Check Compliance Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive compliance automation for financial institutions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* solutions Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive coverage of financial regulatory requirements
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{solution.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent"> Compliance </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Advanced AI technology for regulatory compliance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-emerald-500/50 hover:border-emerald-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-emerald-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Transform compliance operations with intelligent automation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-emerald-500/50 hover:border-emerald-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-emerald-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials
          testimonials={financePolicyCheckTestimonials}
          title="Compliance Success Stories"
          subtitle="See how organizations achieved perfect compliance with AI automation"
          gradientFrom="#10B981"
          gradientVia="#059669"
          gradientTo="#10B981"
          iconColor="#10B981"
          dotColor="bg-emerald-500"
          speed={65}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-emerald-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-green-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Automate Your <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Compliance Processes?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Discover how Finance Policy Check can reduce compliance costs, accelerate policy reviews, and ensure continuous regulatory adherence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-emerald-500/30 hover:border-emerald-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FinancePolicyCheck;